import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { signIn, signUp } from '@/lib/api'
import GoogleSignInButton from './GoogleSignInButton'

// Minimal email/password auth (Google OAuth is wired on the backend; the GIS button
// comes in a later slice). On success it refreshes the `me` query so the app re-gates.
export default function AuthForm() {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErrors([])
    try {
      const fn = mode === 'signIn' ? signIn : signUp
      const { user, errors } = await fn(email, password)
      if (user) {
        // The mutation returns the full current user, so seed the cache directly. This
        // re-gates the app even though the `me` query starts disabled (no token at mount) —
        // invalidate alone wouldn't refetch a disabled query in-session.
        queryClient.setQueryData(['me'], user)
      } else {
        setErrors(errors.length ? errors : ['Something went wrong.'])
      }
    } catch {
      setErrors(['Network error. Is the API running?'])
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mx-auto mt-16 max-w-sm space-y-4">
      <h2 className="text-xl font-semibold">
        {mode === 'signIn' ? 'Sign in' : 'Create your account'}
      </h2>

      <input
        type="email"
        required
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
      />
      <input
        type="password"
        required
        minLength={8}
        placeholder="Password (min 8 chars)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
      />

      {errors.map((err) => (
        <p key={err} className="text-sm text-red-500">{err}</p>
      ))}

      <button
        type="submit"
        disabled={busy}
        className="btn-primary w-full"
      >
        {busy ? '…' : mode === 'signIn' ? 'Sign in' : 'Sign up'}
      </button>

      <button
        type="button"
        onClick={() => setMode(mode === 'signIn' ? 'signUp' : 'signIn')}
        className="w-full text-sm text-gray-500 hover:text-gray-700"
      >
        {mode === 'signIn' ? 'Need an account? Sign up' : 'Have an account? Sign in'}
      </button>

      <GoogleSignInButton onError={(msg) => setErrors([msg])} />
    </form>
  )
}
